/**
 * NGX display Auth Youtube object.
 *
 * Summary of responsibilities:
 *
 *  - handles all functionality related to authenticating users against Googleplus/Youtube
 */
export default (function() {
  // Properties.
  var _config = {
      id:    'Youtube',
      ngxId: 'googleplus',

      authURI:     'https://accounts.google.com/o/oauth2/v2/auth',
      redirectURI: '/display/auth/callback/youtube',

      scopes: [
        'https://www.googleapis.com/auth/youtube',
        'https://www.googleapis.com/auth/plus.me',
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/userinfo.email',
      ],

      authPollMs: 250,
      authMode:   'popup',
    },
    _state = {
      authenticated:    false,
      authResponse:     null,
      identifyResponse: null,
      pollTokenTimeout: null,
      ready:            false,
    },
    _callbacks = {},
    _event = null,
    _buffer = [],
    _authWindow = null,
    _appConfig;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Init.
  var init = function(config) {
      // Merge injected params with our own.
      _.extend(_config, config);

      // Convenience shortcuts.
      _event = NGX.App.Events;
      _appConfig = NGX.App.config;

      // Load JS client library.
      var head = document.getElementsByTagName('head')[0];
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://apis.google.com/js/client.js?onload=gaHandleClientLoaded';
      head.appendChild(script);
    },
    handleClientLoaded = function() {
      // Set API key.
      gapi.client.setApiKey(_config.apiKey);

      // Update ready state.
      _state.ready = true;

      // Execute any buffered calls.
      executeBuffer();
    },
    bufferCall = function(method, args) {
      _buffer.push({ method: method, args: args });
    },
    executeBuffer = function() {
      _.each(_buffer, function(bufferedCall) {
        bufferedCall.method.apply(this, bufferedCall.args);
      });
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Log user in.
    login = function(callbackSuccess, callbackFail, redirectUrl, authRedirectData) {
      // Buffer calls until all lib dependencies are ready.
      if (!_state.ready) {
        bufferCall(login, arguments);
        return;
      }

      // Store reference to callbacks.
      _callbacks.login = {};
      _callbacks.login.success = callbackSuccess;
      _callbacks.login.fail = callbackFail;

      // If we are processing a redirect response.
      if (authRedirectData) {
        handleStatusChange(authRedirectData.responseObject);
      } else {
        // Google Oath client does not allow wildcard redirect_uri values
        // so we need to pass any query string values separately as state.
        // This will then be appended to the redirect url as k/v pair inside the hash fragment.
        var state = JSON.stringify({
          redirectUrl: redirectUrl,
        });

        // Build the auth URL.
        var authURL =
          _config.authURI +
          '?client_id=' +
          _config.appId +
          '&scope=' +
          encodeURIComponent(_config.scopes.join(' ')) +
          '&redirect_uri=' +
          encodeURIComponent(_appConfig.displayURL + _config.redirectURI) +
          '&state=' +
          encodeURIComponent(state) +
          '&response_type=token';

        // Open auth window or redirect based on config.
        switch (_config.authMode) {
          case 'popup':
            _authWindow = window.open(authURL, '', 'width=640, height=480');
            break;

          case 'redirect':
            window.location.href = authURL;
            break;
        }

        // Poll for login response.
        pollLoginResponse();
      }
    },
    pollLoginResponse = function() {
      try {
        // Check if the response object exists.
        if (typeof _authWindow.responseObject !== 'undefined') {
          // If it does, retrieve it and pass off to handler.
          handleStatusChange(_authWindow.responseObject);

          // Attempt to close auth window.
          _authWindow.close();
        } else {
          // Otherwise contiue to poll.
          setTimeout(pollLoginResponse, _config.authPollMs);
        }
      } catch (exception) {
        // Otherwise contiue to poll.
        setTimeout(pollLoginResponse, _config.authPollMs);
      }
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // When a user logged in/out, handle the change.
    handleStatusChange = function(response) {
      // Store response.
      response.href = decodeURIComponent(response.href);
      _state.authResponse = response;

      // If error.
      if (response.hasOwnProperty('error') && response.error !== '') {
        _state.authenticated = false;

        // Execute callback.
        _callbacks.login.fail();
      } else {
        // If success.
        _state.authenticated = true;

        // Get access token hash.
        var accessToken = parseQueryString(response.href, 'access_token');
        var expiresIn = parseQueryString(response.href, 'expires_in');
        var gapiToken = {
          access_token: accessToken,
          expires_in:   expiresIn,
          error:        '',
          state:        '',
        };
        gapi.auth.setToken(gapiToken);

        // Build sanitised response object to pass to success callback.
        var authResponse = {};
        authResponse.network = _config.id;
        authResponse.data = {
          token: accessToken,
        };

        // Execute callback.
        _callbacks.login.success(authResponse);
      }
    },
    parseQueryString = function(queryString, needle) {
      needle = needle.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      var regex = new RegExp('[\\?#&]' + needle + '=([^&#]*)'),
        results = regex.exec(queryString);

      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Identify a user (retrieve as many details about them as we can) after being logged in.
    identify = function(user, callbackSuccess, callbackFail) {
      // Store reference to callbacks.
      _callbacks.identify = {};
      _callbacks.identify.success = callbackSuccess;
      _callbacks.identify.fail = callbackFail;

      var token = user.authedNetworks.Youtube.response.token;

      // Attempt to retrieve users id, name, email address etc.
      gapi.client.load('plus', 'v1', function() {
        gapi.client.plus.people.get({ userId: 'me' }).execute(function(response) {
          // Store response.
          _state.identifyResponse = response;

          // If success.
          if (response && !response.error) {
            // Build sanitised response object to pass to success callback.
            var identifyResponse = {};
            identifyResponse.network = _config.id;

            // Attempt to extract name.
            var firstName = '';
            var lastName = '';
            try {
              if (response.hasOwnProperty('name')) {
                firstName = response.name.givenName;
                lastName = response.name.familyName;
              }
            } catch (exception) {}

            // Attempt to extract email.
            var email = '';
            try {
              if (response.hasOwnProperty('emails')) {
                if (response.emails.length > 0) {
                  email = response.emails[0].value;
                }
              }
            } catch (exception) {}

            // Attempt to extract associated data.
            var associatedData = {};
            associatedData.ngxId = _config.ngxId;
            try {
              // Profile URL.
              if (response.hasOwnProperty('url')) {
                associatedData.profileUrl = response.url;
              }

              // Profile Username.
              if (response.hasOwnProperty('displayName')) {
                associatedData.profileUsername = response.displayName;
              }

              // Avatar URL.
              if (response.hasOwnProperty('image')) {
                if (response.image.hasOwnProperty('isDefault')) {
                  if (response.image.isDefault !== true) {
                    if (response.image.hasOwnProperty('url')) {
                      var avatar = response.image.url;

                      // Attempt to get larger version.
                      avatar = avatar.replace('sz=50', 'sz=150');

                      associatedData.avatarUrl = avatar;
                    }
                  }
                }
              }

              // User id (we now deprecate this value and store it in associatedData instead).
              // Youtube channel id has become the primary id for a user.
              if (response.hasOwnProperty('id')) {
                associatedData.googlePlusId = response.id.toString();
              }
            } catch (exception) {}

            // Also attempt to retrive the users corresponding Youtube channel id.
            gapi.client.load('youtube', 'v3', function() {
              gapi.client.youtube.channels.list({ part: 'snippet', mine: true }).execute(function(response) {
                // Store response.
                _state.identifyResponse.channels = response;

                // If success.
                if (response && !response.error) {
                  var channelId = '';
                  try {
                    if (response.hasOwnProperty('items')) {
                      if (response.items.length > 0) {
                        channelId = response.items[0].id;
                      }
                    }

                    identifyResponse.data = {
                      id:             channelId,
                      firstName:      firstName,
                      lastName:       lastName,
                      email:          email,
                      associatedData: associatedData,
                    };

                    // Execute callback.
                    _callbacks.identify.success(identifyResponse);
                  } catch (exception) {}

                  // If fail.
                } else {
                  // Execute callback.
                  _callbacks.identify.fail();
                }
              });
            });
          } else {
            // If fail.
            // Execute callback.
            _callbacks.identify.fail();
          }
        });
      });
    };

  // Expose public API.
  return {
    init:               init,
    login:              login,
    identify:           identify,
    handleClientLoaded: handleClientLoaded,
    accountRestricted:  false,
  };
})();

// Automatically called by Google lib when loaded, just proxy to real object above.
window.gaHandleClientLoaded = function() {
  NGX.Auth.Providers.Youtube.handleClientLoaded();
};
